import { memo, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  Avatar,
  Box,
  Button,
  Chip,
  CircularProgress,
  IconButton,
  Paper,
  Typography,
  Alert,
  Snackbar,
} from '@mui/material';
import ArrowBackIosNewRoundedIcon from '@mui/icons-material/ArrowBackIosNewRounded';
import FlightRoundedIcon from '@mui/icons-material/FlightRounded';
import GroupsRoundedIcon from '@mui/icons-material/GroupsRounded';
import EmojiEventsRoundedIcon from '@mui/icons-material/EmojiEventsRounded';
import { gamesApi, getSocket, onSocketJoin } from '../../../services/api/gamesApi';
import { authApi } from '../../../services/api/authApi';
import './AviatorScreen.css';
import './FairAviatorScreen.css';

function getMyBet(gameState, playerRows) {
  const active = gameState?.myActiveBets?.[0];
  const queued = gameState?.myQueuedBets?.[0];
  const cashedRow = playerRows.find((r) => r.isMe && r.isCashedOut);
  const cashed = cashedRow || (
    (gameState?.myResult === 'won' || gameState?.myResult === 'cashed')
      ? {
          position: gameState.myPosition,
          cashoutSeconds: gameState.myCashoutSeconds,
          winAmount: gameState.myWinAmount,
          estimatedWin: gameState.myEstimatedPayout,
          isCashedOut: true,
        }
      : null
  );
  const lost = playerRows.find((r) => r.isMe && r.isLost)
    || (gameState?.myResult === 'lost' ? { isLost: true } : null);
  return { active, queued, cashed, lost, betId: active?.id ?? queued?.id ?? null };
}

function positionLabel(rank) {
  if (rank === 1) return '1st';
  if (rank === 2) return '2nd';
  if (rank === 3) return '3rd';
  return `${rank}th`;
}

function rankOfTotal(position, total) {
  if (!position || !total) return '--';
  return `${positionLabel(position)} of ${total}`;
}

function rankHint(position, total) {
  if (!position || !total) return '';
  if (position === 1) return 'Last out · wins most';
  if (position === total) return 'First out · lowest share';
  return '';
}

const MAX_ROWS_PER_TAB = {
  all: 20,
  ranked: 20,
  lost: 15,
};

const CashoutRow = memo(function CashoutRow({ row, cashedOutCount, hint, isMe }) {
  const share = row.winAmount ?? row.estimatedWin;
  return (
    <Box
      className={`fairaviator-cashout-row${isMe ? ' fairaviator-cashout-row--me' : ''}${row.position === 1 ? ' fairaviator-cashout-row--leader' : ''}`}
    >
      <Box className="fairaviator-cashout-row__player">
        <Avatar sx={{ width: 28, height: 28, bgcolor: isMe ? '#f5c518' : '#ff8c00', color: '#1a1000', fontSize: '0.8rem', fontWeight: 800 }}>
          {row.player.slice(0, 1)}
        </Avatar>
        <Box>
          <Typography className="fairaviator-cashout-row__name">{row.player}</Typography>
          {hint ? (
            <Typography className="fairaviator-cashout-row__hint">{hint}</Typography>
          ) : null}
        </Box>
      </Box>
      <Typography className={`fairaviator-cashout-row__rank${row.position === 1 ? ' is-leader' : ''}`}>
        {rankOfTotal(row.position, cashedOutCount)}
      </Typography>
      <Typography className="fairaviator-cashout-row__sec">{row.cashoutSeconds}s</Typography>
      <Typography className="fairaviator-cashout-row__pay">
        {share ? `₹${share.toFixed(2)}` : '…'}
      </Typography>
    </Box>
  );
});

const BetBoardRow = memo(function BetBoardRow({ row, cashedOutCount }) {
  const share = row.winAmount ?? row.estimatedWin;
  return (
    <Box className="bets-list-row fairaviator-list-row">
      <Box className="player-cell">
        <Avatar sx={{ width: 26, height: 26 }}>{row.player.slice(0, 1)}</Avatar>
        <Typography className="fairaviator-bets-name">{row.player}</Typography>
      </Box>
      <Typography className={`fairaviator-bets-rank${row.position === 1 ? ' is-leader' : ''}`}>
        {row.isCashedOut
          ? rankOfTotal(row.position, cashedOutCount)
          : row.isLost
            ? 'Lost'
            : '--'}
      </Typography>
      <Typography className="fairaviator-bets-sec">
        {row.cashoutSeconds ? `${row.cashoutSeconds}s` : '--'}
      </Typography>
      <Typography className={`fairaviator-bets-pay${row.isLost ? ' is-lost' : ''}`}>
        {share ? `₹${share.toFixed(2)}` : row.isLost ? '₹0' : row.isCashedOut ? '…' : '--'}
      </Typography>
    </Box>
  );
});

function FairAviatorScreen({ user, entryFee = 10, onBack, onWalletUpdate }) {
  const [gameState, setGameState] = useState(null);
  const [walletBalance, setWalletBalance] = useState(user?.walletBalance ?? 0);
  const [betId, setBetId] = useState(null);
  const [boardTab, setBoardTab] = useState('ranked');
  const [toast, setToast] = useState('');
  const [loading, setLoading] = useState(false);
  const [lastRoundId, setLastRoundId] = useState(null);
  const [displayFlightSeconds, setDisplayFlightSeconds] = useState(0);
  const flightAnchorRef = useRef({ base: 0, at: 0 });

  const refreshUser = useCallback(async () => {
    try {
      const me = await authApi.me();
      setWalletBalance(me.walletBalance);
      onWalletUpdate?.(me);
      authApi.saveSession({ token: localStorage.getItem('eleven_games_token'), user: me });
    } catch {
      /* ignore */
    }
  }, [onWalletUpdate]);

  useEffect(() => {
    let cancelled = false;
    const refreshState = () =>
      gamesApi
        .fairAviatorState(entryFee)
        .then((state) => {
          if (!cancelled) setGameState(state);
        })
        .catch(() => {});

    refreshState();
    const pollId = setInterval(refreshState, 8000);

    const socket = getSocket();
    const join = () => socket.emit('join:fairaviator', { entryFee });
    const stopJoin = onSocketJoin(join);

    const onState = (state) => {
      if (state.entryFee === entryFee) setGameState(state);
    };

    const onTick = (tick) => {
      if (tick.entryFee !== entryFee) return;
      setGameState((prev) => {
        if (!prev) return prev;
        if (prev.roundId !== tick.roundId) {
          refreshState();
          return prev;
        }
        if (
          prev.phase === tick.phase
          && prev.flightSeconds === tick.flightSeconds
          && prev.waitSeconds === tick.waitSeconds
          && prev.crashSeconds === tick.crashSeconds
          && prev.cashedOutCount === tick.cashedOutCount
          && prev.membersCount === tick.membersCount
          && prev.queueCount === tick.queueCount
          && prev.prizePool === tick.prizePool
        ) {
          return prev;
        }
        return {
          ...prev,
          phase: tick.phase,
          flightSeconds: tick.flightSeconds,
          waitSeconds: tick.waitSeconds,
          crashSeconds: tick.crashSeconds,
          cashedOutCount: tick.cashedOutCount,
          membersCount: tick.membersCount,
          queueCount: tick.queueCount,
          prizePool: tick.prizePool,
          totalPool: tick.totalPool,
        };
      });
    };

    const onVisibility = () => {
      if (document.visibilityState === 'visible') refreshState();
    };

    const onReconnect = () => refreshState();

    socket.on('fairaviator:state', onState);
    socket.on('fairaviator:tick', onTick);
    socket.on('connect', onReconnect);
    document.addEventListener('visibilitychange', onVisibility);

    return () => {
      cancelled = true;
      clearInterval(pollId);
      stopJoin();
      socket.off('fairaviator:state', onState);
      socket.off('fairaviator:tick', onTick);
      socket.off('connect', onReconnect);
      document.removeEventListener('visibilitychange', onVisibility);
    };
  }, [entryFee]);

  const phase = gameState?.phase ?? 'waiting';
  const flightSeconds = gameState?.flightSeconds ?? 0;
  const crashSeconds = gameState?.crashSeconds;
  const waitSeconds = gameState?.waitSeconds ?? 5;
  const membersCount = gameState?.membersCount ?? 0;
  const maxPlayers = gameState?.maxPlayers ?? 100;
  const queueCount = gameState?.queueCount ?? 0;
  const roundNo = gameState?.roundNo ?? 0;
  const recentCrashSeconds = gameState?.recentCrashSeconds ?? [];
  const playerRows = useMemo(() => gameState?.bets ?? [], [gameState?.bets]);
  const myBet = getMyBet(gameState, playerRows);
  const prizePool = gameState?.prizePool ?? 0;
  const cashedOutCount = gameState?.cashedOutCount ?? 0;
  const myEstimatedPayout = gameState?.myEstimatedPayout ?? 0;
  const myWinAmount = gameState?.myWinAmount ?? 0;
  const myResult = gameState?.myResult ?? null;
  const myPosition = gameState?.myPosition;
  const houseCommissionPercent = gameState?.houseCommissionPercent ?? 7;
  const prizePoolPercent = gameState?.prizePoolPercent ?? 90;
  const soloRound = gameState?.soloRound ?? false;
  const soloRefundMessage = gameState?.soloRefundMessage ?? null;

  useEffect(() => {
    if (!gameState?.roundId) return;
    if (lastRoundId && lastRoundId !== gameState.roundId) {
      setBetId(null);
      refreshUser();
    }
    if (phase === 'crashed' && (myResult === 'won' || soloRound)) {
      refreshUser();
    }
    setLastRoundId(gameState.roundId);
  }, [gameState?.roundId, lastRoundId, refreshUser, phase, myResult, soloRound]);

  useEffect(() => {
    const id = myBet.active?.id ?? myBet.queued?.id ?? null;
    if (id) setBetId(id);
    if (phase === 'waiting' && !myBet.active && !myBet.queued && !myBet.cashed) {
      setBetId(null);
    }
  }, [gameState, playerRows, phase, myBet]);

  const animatedFlightSeconds = phase === 'running' ? displayFlightSeconds : flightSeconds;

  useEffect(() => {
    if (phase !== 'running') {
      setDisplayFlightSeconds(flightSeconds);
      return undefined;
    }

    flightAnchorRef.current = { base: flightSeconds, at: performance.now() };
    let raf = 0;
    const step = () => {
      const { base, at } = flightAnchorRef.current;
      setDisplayFlightSeconds(base + (performance.now() - at) / 1000);
      raf = requestAnimationFrame(step);
    };
    raf = requestAnimationFrame(step);
    return () => cancelAnimationFrame(raf);
  }, [phase, flightSeconds, gameState?.roundId]);

  const stagePath = useMemo(() => {
    const elapsedMs = animatedFlightSeconds * 1000;
    const m = 1 + elapsedMs / 5800 + (elapsedMs / 13000) ** 2;
    const progress = Math.min((m - 1) / 8, 1);
    const flightPoint = { x: 8 + progress * 82, y: 12 + progress * 58 };

    const points = [];
    // Keep chart smooth but lighter to repaint on frequent socket updates.
    for (let step = 0; step <= 16; step += 1) {
      const t = step / 16;
      const x = Number((flightPoint.x * t).toFixed(2));
      const yFromBottom = 8 + (flightPoint.y - 8) * (t ** 1.6);
      points.push(`${x},${Number((100 - yFromBottom).toFixed(2))}`);
    }

    return {
      flightPoint,
      curvePoints: points.join(' '),
      fillPolygon: `0,100 ${points.join(' ')} ${flightPoint.x},100`,
    };
  }, [animatedFlightSeconds]);

  const hasBet = !!(myBet.active || myBet.queued);
  const isSpectating = (phase === 'running' || phase === 'crashed') && !hasBet;

  const placeBet = async () => {
    if (myBet.active || myBet.queued) {
      setToast('You already have a bet');
      return;
    }
    if (walletBalance < entryFee) {
      setToast(`Insufficient balance — need ₹${entryFee}`);
      return;
    }

    setLoading(true);
    try {
      const result = await gamesApi.fairAviatorBet({ amount: entryFee, slotIndex: 0 });
      setToast(result.message);
      setBetId(result.betId);
      await refreshUser();
      setGameState(await gamesApi.fairAviatorState(entryFee));
    } catch (err) {
      setToast(err.message);
    } finally {
      setLoading(false);
    }
  };

  const manualCashOut = async () => {
    const id = betId || myBet.active?.id;
    if (!id) {
      setToast('No active bet');
      return;
    }
    setLoading(true);
    try {
      const result = await gamesApi.fairAviatorCashout(id);
      setToast(
        `Cashed out at ${result.cashoutSeconds}s — ${positionLabel(result.position)} place · est. ₹${(result.estimatedPayout ?? 0).toFixed(2)}`,
      );
      setGameState(await gamesApi.fairAviatorState(entryFee));
    } catch (err) {
      setToast(err.message);
    } finally {
      setLoading(false);
    }
  };

  const rankedRows = useMemo(
    () =>
      [...playerRows]
        .filter((r) => r.isCashedOut)
        .sort((a, b) => (a.position ?? 99) - (b.position ?? 99))
        .slice(0, MAX_ROWS_PER_TAB.ranked),
    [playerRows],
  );

  const lostRows = useMemo(
    () => playerRows.filter((r) => r.isLost),
    [playerRows],
  );

  const visibleRows = useMemo(() => {
    const rows =
      boardTab === 'ranked'
        ? rankedRows
        : boardTab === 'lost'
          ? lostRows
          : playerRows;
    const cap = MAX_ROWS_PER_TAB[boardTab] ?? 40;
    return rows.slice(0, cap);
  }, [boardTab, rankedRows, lostRows, playerRows]);

  const totalWin = visibleRows.reduce(
    (s, r) => s + (r.winAmount ?? r.estimatedWin ?? 0),
    0,
  );

  const canCashOut = phase === 'running' && myBet.active && !myBet.cashed;
  const canPlaceBet = !myBet.active && !myBet.queued && !loading;
  const cashedWin = myBet.cashed?.winAmount ?? myBet.cashed?.estimatedWin ?? myWinAmount;

  return (
    <Box className="aviator-root fairaviator-root">
      <Snackbar open={!!toast} autoHideDuration={3000} onClose={() => setToast('')} message={toast} />
      <Box className="aviator-topbar">
        <IconButton size="small" onClick={onBack} sx={{ color: '#fff' }}>
          <ArrowBackIosNewRoundedIcon fontSize="small" />
        </IconButton>
        <Typography className="aviator-logo">
          <span className="jetxpress-brand__jet">jet</span>
          <span className="jetxpress-brand__xpress">Xpress</span>
          {' '}₹{entryFee} #{roundNo}
        </Typography>
        <Box sx={{ display: 'flex', gap: 0.8, flexWrap: 'wrap', justifyContent: 'flex-end' }}>
          <Chip size="small" label={`₹${entryFee} entry`} sx={{ color: '#1a1000', bgcolor: 'rgba(245,197,24,0.9)', fontWeight: 700 }} />
          <Chip size="small" label={`${membersCount}/${maxPlayers}`} sx={{ color: '#fff', bgcolor: 'rgba(255,255,255,0.12)' }} />
          {prizePool > 0 ? (
            <Chip
              size="small"
              label={`Prize ₹${prizePool.toFixed(0)} (${prizePoolPercent}%)`}
              sx={{ color: '#1a1000', bgcolor: 'rgba(245,197,24,0.75)', fontWeight: 700 }}
            />
          ) : null}
          {cashedOutCount > 0 ? (
            <Chip
              size="small"
              icon={<GroupsRoundedIcon sx={{ color: '#1a1000 !important', fontSize: 14 }} />}
              label={`${cashedOutCount} cashed out`}
              sx={{ color: '#1a1000', bgcolor: 'rgba(255,204,80,0.85)', fontWeight: 700 }}
            />
          ) : null}
          {queueCount > 0 ? (
            <Chip size="small" label={`${queueCount} queued`} sx={{ color: '#fff', bgcolor: 'rgba(255,140,0,0.45)' }} />
          ) : null}
          <Chip size="small" label={`Wallet ₹${walletBalance.toFixed(2)}`} sx={{ color: '#fff', bgcolor: 'rgba(255,255,255,0.1)' }} />
        </Box>
      </Box>

      <Box className="fairaviator-banner">
        <Typography className="fairaviator-banner__title">
          Fixed ₹{entryFee} entry — cash out before the crash. <span>Last to cash out gets the highest!</span>
        </Typography>
        <Typography className="fairaviator-banner__sub">
          {soloRound
            ? 'Solo round — 100% entry returned after crash (no platform fee)'
            : `Payouts weighted by seconds in flight · ${houseCommissionPercent}% house fee · crash = you lose`}
        </Typography>
      </Box>

      {soloRound && soloRefundMessage ? (
        <Alert severity="info" sx={{ mx: 1, mt: 0.5, py: 0 }}>
          {soloRefundMessage}
        </Alert>
      ) : null}

      {myBet.queued ? (
        <Alert severity="warning" sx={{ mx: 1, mt: 0.5, py: 0 }}>
          ₹{entryFee} queued for the next round.
        </Alert>
      ) : null}

      {phase === 'running' && isSpectating ? (
        <Alert severity="info" sx={{ mx: 1, mt: 0.5, py: 0 }}>
          Plane flying — {animatedFlightSeconds.toFixed(1)}s · {cashedOutCount} players cashed out.
        </Alert>
      ) : null}

      {phase === 'running' && myBet.active ? (
        <Alert severity="success" sx={{ mx: 1, mt: 0.5, py: 0 }}>
          Cash out before crash! Later cash-out = higher rank &amp; bigger share.
        </Alert>
      ) : null}

      {myEstimatedPayout > 0 && phase !== 'crashed' ? (
        <Alert severity="success" sx={{ mx: 1, mt: 0.5, py: 0 }}>
          {soloRound
            ? `Solo round — you will get ₹${myEstimatedPayout.toFixed(2)} back (100%) when this round ends`
            : `${myPosition ? `${positionLabel(myPosition)} place` : 'Cashed out'} — est. ₹${myEstimatedPayout.toFixed(2)} when round ends`}
        </Alert>
      ) : null}

      {phase === 'crashed' && myResult === 'won' && myWinAmount > 0 ? (
        <Alert
          severity="success"
          icon={<EmojiEventsRoundedIcon />}
          sx={{ mx: 1, mt: 0.5, py: 0.5, fontWeight: 700 }}
        >
          You won ₹{myWinAmount.toFixed(2)}
          {myBet.cashed?.cashoutSeconds ? ` @ ${myBet.cashed.cashoutSeconds}s` : ''}
          {myPosition ? ` · ${positionLabel(myPosition)} place` : ''}
        </Alert>
      ) : null}

      {phase === 'crashed' && myResult === 'lost' ? (
        <Alert severity="error" sx={{ mx: 1, mt: 0.5, py: 0.5 }}>
          You lost ₹{entryFee} — plane crashed before you cashed out
        </Alert>
      ) : null}

      {phase === 'crashed' && soloRound && myEstimatedPayout > 0 ? (
        <Alert severity="success" sx={{ mx: 1, mt: 0.5, py: 0 }}>
          Only one user in plane — ₹{myEstimatedPayout.toFixed(2)} returned (100%, no platform fee)
        </Alert>
      ) : null}

      <Box className="aviator-stage">
        <Box className="aviator-rays" />
        <svg className="aviator-graph-svg" viewBox="0 0 100 100" preserveAspectRatio="none">
          <polygon points={stagePath.fillPolygon} className="aviator-graph-fill" />
          <polyline points={stagePath.curvePoints} className="aviator-graph-line" />
        </svg>
        <Box
          className={phase === 'running' ? 'aviator-plane flying' : 'aviator-plane'}
          style={{ left: `${stagePath.flightPoint.x}%`, bottom: `${stagePath.flightPoint.y}%` }}
        >
          <FlightRoundedIcon />
        </Box>

        {phase === 'waiting' && (
          <Box className="overlay-box">
            <CircularProgress size={24} thickness={5} />
            <Typography className="overlay-title">Next game starts in {waitSeconds}s</Typography>
            <Typography className="overlay-subtitle">
              {membersCount}/{maxPlayers} players · ₹{entryFee} each
            </Typography>
          </Box>
        )}

        <Typography className={`fairaviator-timer ${phase === 'crashed' ? 'crashed' : ''}`}>
          {phase === 'crashed'
            ? `Crashed at ${crashSeconds?.toFixed(1) ?? '?'}s`
            : phase === 'running'
              ? `${animatedFlightSeconds.toFixed(1)}s`
              : 'Ready'}
        </Typography>

        {recentCrashSeconds.length > 0 && (
          <Box className="crash-history-row">
            {recentCrashSeconds.slice(0, 6).map((sec, i) => (
              <Chip
                key={`crash-${i}-${sec}`}
                size="small"
                label={`${sec}s`}
                sx={{ height: 22, fontSize: '0.7rem', bgcolor: 'rgba(245,197,24,0.2)', color: '#f5c518', border: '1px solid rgba(245,197,24,0.35)' }}
              />
            ))}
          </Box>
        )}
      </Box>

      <Box className="aviator-controls fairaviator-controls-single">
        <Paper elevation={0} className="bet-box">
          <Typography variant="caption" className="jetxpress-bet-label">
            Entry fee — fixed ₹{entryFee}
          </Typography>
          <Typography variant="body2" className="jetxpress-bet-status">
            {myBet.cashed
              ? phase === 'crashed'
                ? `${positionLabel(myBet.cashed.position)} · Won ₹${(cashedWin || 0).toFixed(2)} @ ${myBet.cashed.cashoutSeconds}s`
                : `${positionLabel(myBet.cashed.position)} · Cashed @ ${myBet.cashed.cashoutSeconds}s — est. ₹${(cashedWin || 0).toFixed(2)}`
              : myBet.lost
                ? `Lost — plane crashed before you cashed out`
                : myBet.queued
                  ? `Queued ₹${entryFee} for next round`
                  : myBet.active
                    ? phase === 'running'
                      ? `Flying — cash out now!`
                      : 'Bet placed — waiting for takeoff'
                    : phase === 'waiting'
                      ? `Join this round for ₹${entryFee}`
                      : `Queue ₹${entryFee} for next round`}
          </Typography>

          {canCashOut ? (
            <Button
              variant="contained"
              color="warning"
              size="large"
              fullWidth
              disabled={loading}
              onClick={manualCashOut}
              sx={{ fontWeight: 800, fontSize: '0.95rem', animation: 'pulse 1s infinite' }}
            >
              Cash Out @ {animatedFlightSeconds.toFixed(1)}s
            </Button>
          ) : myBet.queued ? (
            <Button
              variant="contained"
              color="success"
              size="large"
              fullWidth
              disabled
              className="jetxpress-bet-queued-btn"
            >
              Queued — ₹{entryFee} for next round
            </Button>
          ) : (
            <Button
              variant="contained"
              color="success"
              size="large"
              fullWidth
              disabled={!canPlaceBet || walletBalance < entryFee}
              onClick={placeBet}
            >
              {loading
                ? 'Placing…'
                : phase === 'waiting'
                  ? `Join — ₹${entryFee}`
                  : `Queue — ₹${entryFee}`}
            </Button>
          )}
        </Paper>
      </Box>

      <Paper elevation={0} className="fairaviator-cashout-panel">
        <Box className="fairaviator-cashout-panel__head">
          <Typography className="fairaviator-cashout-panel__title">
            <EmojiEventsRoundedIcon sx={{ fontSize: 18, mr: 0.5, verticalAlign: 'text-bottom' }} />
            Live cash-out rankings
          </Typography>
          <Typography className="fairaviator-cashout-panel__count">
            {cashedOutCount} of {maxPlayers} cashed out
          </Typography>
        </Box>
        <Typography className="fairaviator-cashout-panel__hint">
          1st rank = last person to cash out (most money). First to cash out gets the lowest rank.
        </Typography>

        <Box className="fairaviator-cashout-panel__header">
          <span>Player</span>
          <span>Rank</span>
          <span>At</span>
          <span>Payout</span>
        </Box>

        <Box className="fairaviator-cashout-panel__list">
          {rankedRows.length === 0 ? (
            <Typography className="fairaviator-cashout-panel__empty">
              No one has cashed out yet — be the first or wait for others.
            </Typography>
          ) : (
            rankedRows.map((row) => (
              <CashoutRow
                key={row.id}
                row={row}
                cashedOutCount={cashedOutCount}
                hint={rankHint(row.position, cashedOutCount)}
                isMe={row.isMe}
              />
            ))
          )}
        </Box>
      </Paper>

      <Paper elevation={0} className="bets-board fairaviator-bets-board">
        <Box className="bets-tabs">
          <Button size="small" variant={boardTab === 'all' ? 'contained' : 'text'} onClick={() => setBoardTab('all')}>All</Button>
          <Button size="small" variant={boardTab === 'ranked' ? 'contained' : 'text'} onClick={() => setBoardTab('ranked')}>
            <EmojiEventsRoundedIcon sx={{ fontSize: 14, mr: 0.3 }} /> Rankings
          </Button>
          <Button size="small" variant={boardTab === 'lost' ? 'contained' : 'text'} onClick={() => setBoardTab('lost')}>Crashed</Button>
        </Box>
        <Box className="bets-summary">
          <Box className="avatar-stack">
            {visibleRows.slice(0, 2).map((row) => (
              <Avatar key={row.id} sx={{ width: 28, height: 28 }}>{row.player.slice(0, 1)}</Avatar>
            ))}
          </Box>
          <Typography className="fairaviator-bets-summary-text">
            {boardTab === 'lost'
              ? `${visibleRows.length} lost`
              : `${rankedRows.length} ranked`}
          </Typography>
          <Typography className="fairaviator-bets-summary-total">₹{totalWin.toFixed(2)}</Typography>
        </Box>
        {(boardTab === 'all' && playerRows.length > MAX_ROWS_PER_TAB.all)
          || (boardTab === 'ranked' && rankedRows.length > MAX_ROWS_PER_TAB.ranked)
          || (boardTab === 'lost' && lostRows.length > MAX_ROWS_PER_TAB.lost) ? (
            <Typography className="fairaviator-bets-cap-note">
              Showing first {MAX_ROWS_PER_TAB[boardTab]} players for smoother live performance.
            </Typography>
          ) : null}
        <Box className="bets-header-row fairaviator-header-row">
          <Typography className="fairaviator-bets-col-head">Player</Typography>
          <Typography className="fairaviator-bets-col-head">Rank</Typography>
          <Typography className="fairaviator-bets-col-head">Sec</Typography>
          <Typography className="fairaviator-bets-col-head">Payout</Typography>
        </Box>
        <Box className="bets-list">
          {visibleRows.length === 0 ? (
            <Typography className="fairaviator-bets-empty">
              {boardTab === 'lost' ? 'No players lost this round.' : 'No players in this list yet.'}
            </Typography>
          ) : (
            visibleRows.map((row) => (
              <BetBoardRow key={row.id} row={row} cashedOutCount={cashedOutCount} />
            ))
          )}
        </Box>
      </Paper>
    </Box>
  );
}

export default FairAviatorScreen;
